import clsx from 'clsx'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { PopupPlacement } from '@/common/types'
import { getValidityStateObject } from '@/common/utils'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'
import { t } from '@/localization/localization'

import DapDSOptionList from '../option-list/option-list.component'
import styles from './combobox.scss?inline'

export default class ComboboxBaseElement extends GenericFormElement {
  @query('input') readonly triggerInput: HTMLInputElement
  @query('dap-ds-option-list') readonly optionList: DapDSOptionList
  @property({ reflect: true }) placement: PopupPlacement = 'bottom-start'
  @property({ type: Boolean, reflect: true }) opened = false
  @property() searchMode: 'none' | 'typehead' | 'autocomplete' | 'manual' =
    'autocomplete'
  @property({ type: Boolean }) search = false
  @property() clearButton?: string = 'true'
  @property() placeholder: string | null
  @property({ type: Boolean }) sync?: boolean
  @property({ type: Boolean }) allowManualInput?: boolean = false
  @property({ type: Boolean }) searchForText?: boolean = false
  @property({ type: String }) searchOnValueSet? = 'false'
  @property({ type: Boolean }) loading?: boolean = false
  @property({ type: String }) selectable?: string = 'true'
  @property({ type: Boolean }) noAnimation?: boolean = false
  @property({ type: Boolean }) textComplete?: boolean = false
  @property({ type: Boolean }) openOnEmpty?: boolean = false
  @property({ type: Boolean }) disableViewportAutoClose: boolean = false
  @property() searchButtonAriaLabel?: string
  @property({ type: Boolean }) subtle: boolean
  @property({ type: Boolean, reflect: true }) multiselect: boolean = false
  @property({ type: Number, reflect: true }) maxSelections?: number

  /** `data-testid` for the clear button. */
  @property() clearButtonTestId = 'combobox-clear-button'

  /** `data-testid` for the search button. */
  @property() searchButtonTestId = 'combobox-search-button'

  /**
   * When enabled, the search button (the trailing icon button in search mode) is
   * automatically disabled while the input is empty and re-enabled as soon as the
   * user types. Opt-in; does not affect the input itself, which stays editable.
   */
  @property({ type: Boolean }) disableSearchWhenEmpty = false

  /** True when the search input currently holds non-whitespace text. */
  private get hasSearchText(): boolean {
    const text =
      this.selectedText || this.selectedItem?.text || this.value || ''
    return text.trim().length > 0
  }

  @state() selectedItem: any
  @state() selectedItems: any[] = []
  @state() currentItem: any
  @state() selectedText: string

  private optionListObserver: MutationObserver | null = null
  private isUserInteraction: boolean = false
  private pendingInitialValues: string[] = []

  static readonly styles = unsafeCSS(styles)

  protected async firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): Promise<void> {
    super.firstUpdated(_changedProperties)

    await this.optionList.updateComplete

    // Wait for next frame to ensure slotted options are available
    await new Promise(resolve => requestAnimationFrame(resolve))

    // Wait for all option items to complete their updates
    const options = this.getComboboxOptions()

    await Promise.all(options.map((opt: any) => opt.updateComplete))

    // Get the underlying value, not the computed value from the getter
    const underlyingValue = super.value

    if (underlyingValue) {
      if (this.multiselect) {
        // Handle multiselect mode - parse comma-separated values
        const values = underlyingValue
          .split(',')
          .map((v: string) => v.trim())
          .filter((v: string) => v)
        // Store initial values to process later when options are ready
        this.pendingInitialValues = values
        // Try to populate immediately if options are available
        this.updateMultipleSelectionsFromValues(values)
      } else {
        // Handle single-select mode
        // Try to find the selected item from available options
        this.selectedItem = this.optionList
          ?.getAllOptions()
          .find(option => option.value === underlyingValue)

        if (this.selectedItem) {
          this.selectedText = this.selectedItem.optionText || ''
        } else {
          // If no selected item found, leave selectedText empty
          // It will be updated when options are loaded via updateSelectionFromValue
          this.selectedText = ''
        }

        if (
          !this.selectedText &&
          (this.allowManualInput || this.searchMode === 'none')
        ) {
          this.selectedText = underlyingValue
        }
      }
    }

    // Set up MutationObserver to watch for dynamic option changes
    this.setupOptionListObserver()
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    // Handle value property changes from outside the component
    // Only process for combobox components, not search components
    // Skip multiselect mode as the setter already handles value updates
    if (
      _changedProperties.has('value') &&
      this.triggerInput &&
      !this.search &&
      !this.multiselect
    ) {
      const newValue = this.value
      if (newValue) {
        this.handleValueChange(newValue)
      } else {
        this.clearSelection()
      }
    }

    // Free-text search (searchMode 'none' or allowManualInput) has no option to
    // map onto, so the branch above skips it and the value never reaches the
    // uncontrolled input. Reflect an externally-set value straight into the field
    // so the search box is controllable (e.g. restoring a saved query). Typehead/
    // autocomplete search keeps its existing behaviour. The guard leaves the
    // input untouched when it already matches, so a keystroke — which set the
    // value in the first place — doesn't bounce the caret to the end.
    if (
      _changedProperties.has('value') &&
      this.triggerInput &&
      this.isFreeTextSearch
    ) {
      const newValue = this.value ?? ''
      if (this.triggerInput.value !== newValue) {
        this.triggerInput.value = newValue
      }
      this.selectedText = newValue
    }
  }

  private get isFreeTextSearch(): boolean {
    return (
      Boolean(this.search) &&
      (this.searchMode === 'none' || Boolean(this.allowManualInput))
    )
  }

  /**
   * Handles value changes from outside the component
   * This ensures the selection state is updated when the value is set programmatically
   */
  private handleValueChange(newValue: string): void {
    if (!newValue) return

    // Mark this as programmatic change, not user interaction
    this.isUserInteraction = false

    // Try to find the option with the new value
    const option = this.getComboboxOptions().find(opt => opt.value === newValue)

    if (option) {
      // Option found, update selection
      this.selectedItem = option
      this.selectedText = option.optionText || option.textContent || ''

      // Update the input value
      if (this.triggerInput) {
        this.triggerInput.value = this.selectedText
      }

      // Trigger re-render
      this.requestUpdate()
    } else {
      // Option not found yet (might be loading)
      this.selectedItem = null

      // For search components, preserve the selectedText to maintain search functionality
      // For combobox components, clear it to avoid showing raw values
      if (!this.search) {
        this.selectedText = ''

        // Clear input value
        if (this.triggerInput) {
          this.triggerInput.value = ''
        }
      }

      // Trigger re-render
      this.requestUpdate()
    }
  }

  private setupOptionListObserver(): void {
    if (!this.optionList) return

    // Disconnect existing observer if any
    if (this.optionListObserver) {
      this.optionListObserver.disconnect()
    }

    // Create new observer to watch for changes in the option list
    this.optionListObserver = new MutationObserver(mutations => {
      let shouldUpdateState = false

      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          // Check if option items were added or removed
          const addedOptions = Array.from(mutation.addedNodes).some(
            node => node.nodeName === 'DAP-DS-OPTION-ITEM',
          )
          const removedOptions = Array.from(mutation.removedNodes).some(
            node => node.nodeName === 'DAP-DS-OPTION-ITEM',
          )

          if (addedOptions || removedOptions) {
            shouldUpdateState = true
          }
        }
      })

      if (shouldUpdateState) {
        // Update combobox state based on new option list
        this.updateComboboxState()
      }
    })

    // Start observing the combobox element itself for slot changes
    // This is needed because remote data is added directly to the combobox element
    this.optionListObserver.observe(this, {
      childList: true,
      subtree: true,
    })
  }

  private updateComboboxState(): void {
    // Wait for the next tick to ensure DOM is updated
    requestAnimationFrame(() => {
      // Wait one more tick to ensure options are fully initialized
      requestAnimationFrame(() => {
        const hasOptions = this.getComboboxOptions().length > 0
        const hasInputValue =
          this.triggerInput?.value && this.triggerInput.value.trim() !== ''

        // If we have options and the dropdown should be open, show it
        // For search components (this.search = true) or autocomplete mode
        // Only open if this is a user interaction, not programmatic value setting
        if (
          hasOptions &&
          hasInputValue &&
          (this.isAutoComplete || this.search) &&
          this.isUserInteraction
        ) {
          if (!this.opened) {
            this.showDropDown()
          }
        } else if (!hasOptions && !this.openOnEmpty && hasInputValue) {
          // If no options and we don't allow empty, hide the dropdown
          // But only if the user has actually typed something
          this.hideDropDown()
        }

        // Handle value restoration when options become available
        if (hasOptions) {
          if (this.multiselect) {
            // For multiselect, check if selectedItems needs to be populated
            if (
              this.selectedItems.length === 0 &&
              this.pendingInitialValues.length > 0
            ) {
              this.updateMultipleSelectionsFromValues(this.pendingInitialValues)
            }
          } else {
            // For single-select, check if selectedItem needs to be populated
            if (this.value && !this.selectedItem) {
              this.updateSelectionFromValue()
            }
          }
        }
      })
    })
  }

  /**
   * Updates the selection state based on the current value
   * This is useful when options are loaded asynchronously
   */
  private updateSelectionFromValue(): void {
    if (!this.value) return

    // Mark this as programmatic change, not user interaction
    this.isUserInteraction = false

    const option = this.getComboboxOptions().find(
      opt => opt.value === this.value,
    )

    if (option) {
      this.selectedItem = option
      this.selectedText = option.optionText || option.textContent || ''

      // Update the input value to show the selected text
      if (this.triggerInput) {
        this.triggerInput.value = this.selectedText
      }

      // Trigger re-render to update clear icon visibility
      this.requestUpdate()
    }
  }

  private cleanupOptionListObserver(): void {
    if (this.optionListObserver) {
      this.optionListObserver.disconnect()
      this.optionListObserver = null
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.cleanupOptionListObserver()
  }

  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    if (name === 'value' && !value) {
      this.clearSelection()
    }

    super.attributeChangedCallback(name, _old, value)
  }

  get isAutoComplete() {
    return this.searchMode === 'autocomplete' || this.searchMode === 'manual'
  }

  /**
   * Get all option items directly from the combobox element
   * This is more reliable for dynamic updates than the option list component
   */
  private getComboboxOptions(): any[] {
    return Array.from(this.querySelectorAll('dap-ds-option-item'))
  }

  get isOpenOnEmpty() {
    return (
      this.openOnEmpty ||
      (!this.openOnEmpty && this.getComboboxOptions().length > 0)
    )
  }

  get isOpenOnAutocomplete() {
    return this.isAutoComplete && this.isOpenOnEmpty
  }

  get shouldHideClearIcon() {
    // Check if input is empty (cross-browser compatible)
    const inputValue = this.triggerInput?.value || ''
    const hasPlaceholder = this.placeholder && this.placeholder.trim() !== ''

    // Only apply JavaScript fallback when there's no placeholder
    // When there IS a placeholder, let CSS :placeholder-shown handle it
    if (hasPlaceholder) {
      return false // Let CSS handle it
    }

    // Hide clear icon when input is empty and has no placeholder
    return inputValue === ''
  }

  handleClick = () => {
    if (!this.getComboboxOptions().length && !this.openOnEmpty) {
      this.hideDropDown()
    } else {
      this.opened ? this.hideDropDown() : this.showDropDown()
    }
  }

  private async handleEnterKey(event: KeyboardEvent) {
    event.preventDefault()
    event.stopPropagation()

    if (this.allowManualInput) {
      return this.handleSearchEnter()
    }

    if (this.currentItem && this.opened) {
      return this.handleCurrentItemEnter()
    }

    if (this.search && !this.isAutoComplete) {
      return this.handleSearchEnter()
    }

    return this.handleDefaultEnter()
  }

  private async handleCurrentItemEnter() {
    if (this.currentItem.value === this.selectedItem?.value) {
      this.hideDropDown()
      this.triggerInput.value = this.selectedItem.text
      this.triggerInput.focus()
      return
    }

    if (this.currentItem.value !== this.selectedItem?.value) {
      if (!this.searchForText) {
        await this.handleSelectedItemChange(this.currentItem)
      } else {
        await this.handleTextSearch(this.currentItem.text)
      }
    }
  }

  private async handleSearchEnter() {
    this.emit('dds-search', { search: this.selectedText })
    this.setValidity()
    return this.hideDropDown()
  }

  private async handleDefaultEnter() {
    if (this.opened) {
      return this.hideDropDown()
    } else {
      // For autocomplete mode, allow opening even without options initially
      // The MutationObserver will handle showing/hiding based on actual option availability
      return (
        (this.openOnEmpty ||
          this.getComboboxOptions().length ||
          this.isAutoComplete) &&
        this.showDropDown()
      )
    }
  }

  private async handleSelectedItemChange(item: any) {
    this.selectedItem = item
    this.emit('dds-change', {
      value: this.selectedItem.value,
      selectedItem: this.selectedItem,
      type: 'select',
    })
    await this.updateComplete
    this.value = this.selectedItem.value
    this.selectedText = this.currentItem.text
    this.triggerInput.value = this.currentItem.text
    this.triggerInput.focus()
    this.setValidity()
  }

  private async handleTextSearch(text: string) {
    this.value = text
    this.selectedText = text
    this.triggerInput.value = text
    this.triggerInput.focus()
    this.emit('dds-search', { search: text })
    this.setValidity()
  }

  private async handleArrowKeys(event: KeyboardEvent) {
    event.preventDefault()

    if (!this.opened) {
      // For autocomplete mode, allow opening even without options initially
      // The MutationObserver will handle showing/hiding based on actual option availability
      if (
        this.openOnEmpty ||
        this.getComboboxOptions().length ||
        this.isAutoComplete
      ) {
        this.showDropDown()
      } else {
        return // Don't proceed if we can't open dropdown
      }
    }

    await this.updateComplete
    this.optionList.captureFocus(event.key)
  }

  handleKeyDown = async (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      return
    }

    if (event.key === 'Enter') {
      return this.handleEnterKey(event)
    }

    if (event.key === 'Escape') {
      this.hideDropDown()
      return this.focus()
    }

    // Handle Backspace for chip removal in multiselect mode
    if (event.key === 'Backspace' && this.multiselect) {
      const inputValue = this.triggerInput?.value || ''
      // Only remove chip if input is empty
      if (inputValue === '' && this.selectedItems.length > 0) {
        event.preventDefault()
        const lastItem = this.selectedItems[this.selectedItems.length - 1]
        this.removeSelectedItem(lastItem)
        this.emit('dds-change', {
          value: this.selectedItems.map(item => item.value).join(','),
          selectedItems: this.selectedItems,
          type: 'remove',
          item: lastItem,
        })
        this.setValidity()
        return
      }
    }

    if (
      (event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight') &&
      this.isAutoComplete
    ) {
      return this.handleArrowKeys(event)
    }

    // For autocomplete mode, only show dropdown if there are options OR if openOnEmpty is true
    if (this.isAutoComplete && this.triggerInput?.value && !this.opened) {
      const hasOptions = this.getComboboxOptions().length > 0
      if (this.openOnEmpty || hasOptions) {
        this.showDropDown()
      }
    }
  }

  handleInput = async (event: InputEvent) => {
    const input = this.triggerInput

    // Mark this as user interaction
    this.isUserInteraction = true
    if (
      event.inputType !== 'deleteContentBackward' ||
      (event.inputType === 'deleteContentBackward' && this.allowManualInput)
    ) {
      this.selectedText = input.value
      if (this.search) {
        this.value = input.value
      }
    } else if (this.search) {
      this.selectedText = input.value
      this.value = input.value
    } else if (
      input.value === '' &&
      event.inputType === 'deleteContentBackward'
    ) {
      this.clearSelection()
    }

    this.emit('dds-input', { input: input.value, originalEvent: event })
    this.setValidity()

    // Trigger re-render to update clear icon visibility
    this.requestUpdate()

    // For autocomplete mode, only show dropdown if there are options OR if openOnEmpty is true
    if (this.isAutoComplete && input.value && !this.opened) {
      const hasOptions = this.getComboboxOptions().length > 0
      if (this.openOnEmpty || hasOptions) {
        this.showDropDown()
      }
    }
  }

  async clearSelection() {
    // Mark this as programmatic change, not user interaction
    this.isUserInteraction = false

    // Was there anything to clear? Clearing an already-empty control (e.g. a
    // programmatic `value = ''` on mount) must stay silent — otherwise it fires
    // dds-change as if the user cleared a value, which a consumer reads as an
    // edit. A real clear (the × button on a filled field) still emits.
    const hadValue = Boolean(
      this.value || this.selectedText || this.triggerInput?.value,
    )

    if (this.triggerInput) {
      this.triggerInput.value = ''
    }
    this.selectedText = ''
    this.selectedItem = null

    this.value = undefined

    // Trigger re-render to update clear icon visibility
    this.requestUpdate()

    await this.updateComplete

    if (hadValue) {
      this.emit('dds-change', this.selectedItem)
    }
  }

  /**
   * Multiselect helper methods
   */
  private getSelectedValues(): string[] {
    return this.selectedItems.map(item => item.value)
  }

  private isMaxSelectionsReached(): boolean {
    if (!this.maxSelections) return false
    return this.selectedItems.length >= this.maxSelections
  }

  private addSelectedItem(item: any): void {
    if (!this.multiselect) return

    // Check if item is already selected
    const isAlreadySelected = this.selectedItems.some(
      selected => selected.value === item.value,
    )

    if (isAlreadySelected) return

    // Check max selections limit
    if (this.isMaxSelectionsReached()) return

    // Add item to selectedItems array
    this.selectedItems = [...this.selectedItems, item]

    // Update value to comma-separated string (use super to avoid setter loop)
    const newValue = this.getSelectedValues().join(',')
    super.value = newValue

    // Clear input text
    this.selectedText = ''
    if (this.triggerInput) {
      this.triggerInput.value = ''
    }

    // Trigger re-render
    this.requestUpdate()
  }

  private removeSelectedItem(item: any): void {
    if (!this.multiselect) return

    // Remove item from selectedItems array
    this.selectedItems = this.selectedItems.filter(
      selected => selected.value !== item.value,
    )

    // Update value to comma-separated string (use super to avoid setter loop)
    if (this.selectedItems.length === 0) {
      super.value = undefined
    } else {
      const newValue = this.getSelectedValues().join(',')
      super.value = newValue
    }

    // Trigger re-render
    this.requestUpdate()
  }

  private clearAllSelections(): void {
    if (!this.multiselect) return

    // Clear all selections
    this.selectedItems = []
    super.value = undefined
    this.selectedText = ''

    if (this.triggerInput) {
      this.triggerInput.value = ''
    }

    // Trigger re-render
    this.requestUpdate()
  }

  private updateMultipleSelectionsFromValues(values: string[]): void {
    if (!this.multiselect) return

    const options = this.getComboboxOptions()

    // Don't clear if we've already populated items
    const shouldClear = this.selectedItems.length === 0
    if (shouldClear) {
      this.selectedItems = []
    }

    let foundAny = false
    values.forEach(value => {
      // Check both property and attribute since elements might not be fully upgraded
      const option = options.find(
        opt => opt.value === value || opt.getAttribute('value') === value,
      )
      if (option) {
        // Check if already in selectedItems to avoid duplicates
        const exists = this.selectedItems.some(item => item.value === value)
        if (!exists) {
          this.selectedItems.push(option)
          foundAny = true
        }
      }
    })

    // Clear pending values if we successfully found items
    if (foundAny && this.pendingInitialValues.length > 0) {
      this.pendingInitialValues = []
    }

    // Trigger re-render
    this.requestUpdate()
  }

  /**
   * Override value getter/setter to handle multiselect mode
   */
  override get value(): string | undefined {
    if (this.multiselect) {
      const values = this.getSelectedValues()
      const result = values.length > 0 ? values.join(',') : undefined
      return result
    }
    return super.value
  }

  override set value(val: string | undefined) {
    if (this.multiselect) {
      // Parse comma-separated values
      const values = val
        ? val
            .split(',')
            .map((v: string) => v.trim())
            .filter((v: string) => v)
        : []

      // Store as pending if we have values and selectedItems is empty
      // This ensures initial values are processed even if set after component is created
      if (values.length > 0 && this.selectedItems.length === 0) {
        this.pendingInitialValues = values
      }

      this.updateMultipleSelectionsFromValues(values)

      // Update parent value
      super.value = val
    } else {
      // Single select mode - use parent implementation
      super.value = val
    }
  }

  handleListKeyDown = (event: CustomEvent) => {
    if (event.detail.key === 'Escape') {
      this.hideDropDown()
      this.currentItem = this.selectedItem
      this.selectedText = ''
      this.triggerInput.value = this.selectedItem.text
      this.triggerInput.focus()
    }
  }

  handleDocumentMouseDown = (event: MouseEvent) => {
    const path = event.composedPath()
    if (this && !path.includes(this)) {
      this.hideDropDown()
    }
  }

  private readonly handlePopupAutoClose = () => {
    this.hideDropDown({ skipFocus: true })
  }

  handleFocus = () => {
    this.emit('dds-focus')
  }

  handleBlur = () => {
    this.emit('dds-blur')
  }

  async showDropDown() {
    if (this.disabled || this.readonly || !this.isAutoComplete || this.opened) {
      return
    }

    // For autocomplete mode, allow opening even without options initially
    // The MutationObserver will handle showing/hiding based on actual option availability
    if (
      !this.openOnEmpty &&
      !this.getComboboxOptions().length &&
      !this.isAutoComplete
    ) {
      return
    }

    this.opened = true

    document.addEventListener('mousedown', this.handleDocumentMouseDown)
  }

  async hideDropDown(options?: { skipFocus?: boolean }) {
    if (!this.opened) {
      return
    }

    this.opened = false

    document.removeEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
    if (!options?.skipFocus) {
      this.focus()
    }
    this.clearSelectionRange()
  }

  public get focusElement() {
    return this.triggerInput
  }

  handleSelectedChange = async (event: CustomEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (this.searchForText) {
      this.selectedText = event.detail.text
      this.value = event.detail.text
      this.triggerInput.value = event.detail.text

      this.emit('dds-search', { search: this.selectedText })

      // Trigger re-render to update clear icon visibility
      this.requestUpdate()

      this.setValidity()
    } else if (this.multiselect) {
      // Multiselect mode - add item to selected items
      this.addSelectedItem(event.detail)
      this.emit('dds-change', {
        value: this.selectedItems.map(item => item.value).join(','),
        selectedItems: this.selectedItems,
        type: 'add',
        item: event.detail,
      })

      // Clear current item to prevent text completion from filling it back in
      this.currentItem = null

      // Wait for update to complete before focusing
      await this.updateComplete

      // Ensure input is cleared after render
      this.triggerInput.value = ''
      this.triggerInput.focus()
      this.setValidity()

      // Don't close dropdown in multiselect mode
      return
    } else {
      this.selectedItem = event.detail
      this.selectedText = event.detail.text

      if (this.value !== this.selectedItem?.value) {
        this.triggerInput.value = event.detail.text
        this.value = this.selectedItem.value
        this.emit('dds-change', {
          value: this.selectedItem.value,
          selectedItem: this.selectedItem,
          type: 'select',
        })

        // Trigger re-render to update clear icon visibility
        this.requestUpdate()

        await this.updateComplete

        if (this.currentItem) {
          this.triggerInput.value = this.currentItem.text
        }
        this.triggerInput.focus()
        this.setValidity()
      }
    }

    this.hideDropDown()
  }

  handleOptionDeselect = (event: CustomEvent) => {
    event.preventDefault()
    event.stopPropagation()

    this.selectedItem = null
    this.selectedText = ''
    this.value = undefined
    this.triggerInput.value = ''
    this.emit('dds-change', {
      value: undefined,
      selectedItem: null,
      type: 'deselect',
    })

    // Trigger re-render to update clear icon visibility
    this.requestUpdate()

    this.setValidity()
  }

  handleCurrentOptionChange = (event: CustomEvent) => {
    this.currentItem = event.detail

    // Skip text completion in multiselect mode
    if (this.multiselect) {
      return
    }

    if (event.detail) {
      if (
        this.textComplete &&
        this.searchMode !== 'manual' &&
        this.searchMode !== 'none'
      ) {
        this.triggerInput.value = event.detail.text
      }

      if (
        this.isAutoComplete &&
        this.selectedText &&
        this.selectedText !== '' &&
        !this.allowManualInput &&
        this.textComplete
      ) {
        this.triggerInput.value = event.detail.text
        this.setSelectionRange(
          this.selectedText.length,
          this.triggerInput.value.length,
        )
      }
    }
  }

  handleListChanged = (event: CustomEvent) => {
    if (!event.detail.elements.length && !this.openOnEmpty) {
      this.hideDropDown()
    }
  }

  handleClearClick = () => {
    if (!this.disabled) {
      if (this.multiselect) {
        this.clearAllSelections()
      } else {
        this.clearSelection()
      }

      this.triggerInput.focus()
      this.setValidity()

      if (!this.openOnEmpty) {
        this.hideDropDown()
      }

      this.emit('dds-clear')
    }
  }

  handleSearchClick = (event: MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()

    this.setValidity()
    this.emit('dds-search', { search: this.selectedText })
  }

  private setSelectionRange(start: number, end: number) {
    this.triggerInput.setSelectionRange(start, end)
  }

  private clearSelectionRange() {
    const controlValueLength = this.triggerInput.value.length

    this.setSelectionRange(controlValueLength, controlValueLength)
  }

  getValidity() {
    if (this.multiselect) {
      return this.getMultiSelectValidity()
    }
    if (this.isAutoComplete) {
      return this.getAutoCompleteValidity()
    }
    return this.getBasicValidity()
  }

  formResetCallback() {
    this.internals.setValidity({})
    this.internals.setFormValue(this.defaultValue)
    this.value = this.defaultValue
    if (this.multiselect) {
      this.selectedItems = []
    } else {
      this.selectedItem = this.defaultValue
    }
  }

  private getMultiSelectValidity(): boolean {
    // Valid if not required OR has at least one selection
    return !this.required || this.selectedItems.length > 0
  }

  private getAutoCompleteValidity(): boolean {
    if (this.allowManualInput) {
      return this.getManualInputValidity()
    }
    if (this.searchForText) {
      return this.getSearchTextValidity()
    }
    return this.getSelectedItemValidity()
  }

  private getManualInputValidity(): boolean {
    return (
      this.triggerInput.value !== '' ||
      (this.selectedItem !== null && this.selectedItem !== undefined)
    )
  }

  private getSearchTextValidity(): boolean {
    return this.triggerInput.value !== ''
  }

  private getSelectedItemValidity(): boolean {
    return !!this.selectedItem
  }

  private getBasicValidity(): boolean {
    return this.triggerInput.value !== ''
  }

  // custom validation
  get validity(): ValidityState {
    if (this.internals.validity) {
      return this.internals.validity
    } else {
      return getValidityStateObject({
        valueMissing: this.required && !this.getValidity(),
      })
    }
  }

  get validationMessage(): string {
    return t('form.validationMessage.select')
  }

  checkValidity() {
    return this.required ? this.getValidity() : true
  }

  reportValidity() {
    return this.required ? this.checkValidity() : true
  }

  setValidity() {
    if (!this.checkValidity()) {
      this.internals.setValidity(
        {
          valueMissing: this.required && !this.getValidity(),
        },
        this.validationMessage,
        this.triggerInput,
      )
    } else {
      this.internals.setValidity({})
    }
  }

  handleInvalid(event: Event) {
    event.preventDefault()
  }

  getActiveDescendant() {
    if (!this.opened) {
      return this.triggerInput
    }

    const activeOption = this.optionList?.getActiveOption()
    return activeOption
  }

  getSearchIconSize(size: string) {
    switch (size) {
      case 'sm':
        return 'md'
      case 'lg':
        return 'lg'
      case 'xs':
        return 'sm'
      default:
        return 'md'
    }
  }

  getInnerIconSize(size: string) {
    switch (size) {
      case 'sm':
        return 16
      case 'lg':
        return 16
      case 'xs':
        return 10
      default:
        return 16
    }
  }

  private handleChipRemove = (event: CustomEvent, item: any) => {
    event.stopPropagation()
    this.removeSelectedItem(item)
    this.emit('dds-change', {
      value: this.selectedItems.map(item => item.value).join(','),
      selectedItems: this.selectedItems,
      type: 'remove',
      item: item,
    })
    this.setValidity()

    // Focus input after removal
    this.triggerInput.focus()
  }

  private renderChip(item: any) {
    // Get text from the option element - prefer optionText getter, then textContent
    const text = item.optionText || item.textContent || item.text || ''

    return html`
      <dap-ds-chip
        size="sm"
        removeable
        .value=${item.value}
        @dds-remove=${(e: CustomEvent) => this.handleChipRemove(e, item)}>
        ${text}
      </dap-ds-chip>
    `
  }

  private renderChips() {
    if (!this.multiselect || this.selectedItems.length === 0) {
      return nothing
    }

    return html`
      <div class="combobox__chips" part="chips">
        ${this.selectedItems.map(item => this.renderChip(item))}
      </div>
    `
  }

  private renderInput() {
    // In multiselect mode, only show selectedText (typing), never show selectedItem
    const inputValue = this.multiselect
      ? this.selectedText
      : this.selectedText !== ''
        ? this.selectedText
        : this.selectedItem?.text || ''

    return html`<input
      id="input"
      part="input"
      role="combobox"
      class="${clsx('input', `input--${this.size}`, {
        'input--disabled': this.disabled,
        'input--readonly': this.readonly,
        'input--multiselect': this.multiselect,
      })}"
      aria-expanded=${this.opened}
      aria-haspopup="listbox"
      aria-labelledby="label"
      aria-describedby="description ${this.feedback ? 'feedback' : ''}"
      aria-disabled=${this.disabled}
      aria-autocomplete="both"
      aria-controls="option-list"
      aria-activedescendant=${ifDefined(this.getActiveDescendant()?.id)}
      aria-multiselectable=${this.multiselect ? 'true' : 'false'}
      ?required=${this.required}
      ?readonly=${this.readonly}
      ?autofocus=${this.autofocus}
      autocomplete=${this.autocomplete as any}
      ?disabled=${this.disabled}
      value=${inputValue}
      placeholder=${ifDefined(this.placeholder ?? '')}
      type=${this.search ? 'search' : 'text'}
      @blur=${this.handleBlur}
      @focus=${this.handleFocus}
      @input=${this.handleInput}
      @keydown=${this.handleKeyDown}
      @click=${this.handleClick} />`
  }

  private renderClearButton() {
    if (this.clearButton !== 'true' || this.readonly) return nothing

    return html`<div
      class="${clsx('clear-icon', {
        'clear-icon--hidden': this.shouldHideClearIcon,
      })}">
      <dap-ds-icon-button
        icon="close-line"
        data-testid=${this.clearButtonTestId}
        aria-label=${t('clear')}
        ?disabled=${this.disabled}
        @click=${this.handleClearClick}></dap-ds-icon-button>
    </div>`
  }

  private renderIndicatorIcon() {
    const isAutocompleteOrManual =
      this.searchMode === 'autocomplete' || this.searchMode === 'manual'
    const showLoadingSpinner = this.loading
      ? html`<dap-ds-icon-loading-spinner></dap-ds-icon-loading-spinner>`
      : null

    if (isAutocompleteOrManual && !this.search) {
      return html`<div
        class="${clsx('indicator-icon', {
          'indicator-icon--loading': this.loading,
        })}"
        part="indicator-icon">
        ${showLoadingSpinner ||
        html`<slot name="indicator-icon"
          ><dap-ds-icon-arrow-down-s-line
            aria-hidden="true"
            @click=${this.handleClick}>
          </dap-ds-icon-arrow-down-s-line
        ></slot>`}
      </div>`
    }

    if (this.search) {
      return html`<div
        class="${clsx('indicator-icon', {
          'indicator-icon--loading': this.loading,
        })}">
        ${showLoadingSpinner ||
        html`<dap-ds-icon-button
          part="search-button"
          size=${this.getSearchIconSize(this.size)}
          variant=${this.subtle ? 'neutral' : 'inverted-brand'}
          icon="search-line"
          ?disabled=${this.disableSearchWhenEmpty && !this.hasSearchText}
          data-testid=${this.searchButtonTestId}
          iconSize=${this.getInnerIconSize(this.size)}
          aria-label=${this.searchButtonAriaLabel ?? t('search')}
          @click=${this.handleSearchClick}>
        </dap-ds-icon-button>`}
      </div>`
    }

    return nothing
  }

  private renderSelectActions() {
    return html`<div
      class="${clsx('select-action', `select-action--${this.size}`, {
        'select-action--disabled': this.disabled,
        'select-action--readonly': this.readonly,
        'select-action--opened': this.opened,
        'select-action--multiselect': this.multiselect,
      })}">
      ${this.renderClearButton()} ${this.renderIndicatorIcon()}
    </div>`
  }

  private renderOptionList() {
    return html`<dap-ds-option-list
      class="option-list"
      part="option-list"
      id="option-list"
      .search=${this.search}
      searchMode=${this.allowManualInput ? 'none' : this.searchMode}
      searchOnValueSet=${this.searchOnValueSet === 'true'}
      filterText=${this.selectedText}
      size=${ifDefined(this.size)}
      value=${ifDefined(this.value)}
      selectable=${ifDefined(this.selectable)}
      @dds-option-change=${this.handleSelectedChange}
      @dds-option-deselect=${this.handleOptionDeselect}
      @dds-keydown=${this.handleListKeyDown}
      @dds-current-change=${this.handleCurrentOptionChange}
      @dds-list-changed=${this.handleListChanged}>
      <slot></slot>
    </dap-ds-option-list>`
  }

  renderCombobox() {
    return html` ${(this, this.renderLabel())}
      <div>
        <dap-ds-popup
          part="base"
          class="select-popup"
          overflow
          ?opened=${this.opened}
          ?hasArrow=${false}
          placement=${this.placement}
          floatingStrategy="fixed"
          offset="4"
          ?sync=${this.sync}
          ?disableViewportAutoClose=${this.disableViewportAutoClose}
          @dds-close=${this.handlePopupAutoClose}
          @dds-closed=${this.hideDropDown}
          @dds-opened=${this.showDropDown}>
          <div
            slot="trigger"
            part="trigger"
            class="${clsx('select', `select--${this.size}`, {
              'select--disabled': this.disabled,
              'select--readonly': this.readonly,
              'select--opened': this.opened,
              'select--loading': this.loading,
              'select--success': this.status === 'success',
              'select--error': this.status === 'error',
              'select--no-animation': this.noAnimation,
              'select--multiselect': this.multiselect,
            })}">
            ${this.renderChips()} ${this.renderInput()}
            ${this.renderSelectActions()}
          </div>
          ${this.renderOptionList()}
        </dap-ds-popup>
        ${this.multiselect
          ? html`<div
              class="sr-only"
              role="status"
              aria-live="polite"
              aria-atomic="true">
              ${this.selectedItems.length > 0
                ? t('combobox.multiselect.selected', {
                    count: this.selectedItems.length,
                  })
                : ''}
            </div>`
          : nothing}
        ${this.feedback &&
        html`<dap-ds-feedback
          part="feedback"
          feedbackType=${ifDefined(this.feedbackType)}
          feedbackSize="${ifDefined(this.size as 'lg' | 'sm' | 'xs')}"
          id="feedback">
          ${this.feedback}
        </dap-ds-feedback>`}
      </div>`
  }
}
